<script>
import { MOLDURA_DO_THREAD } from '@modules/aawz-intelligence-v2/ui-registry/moldura-do-thread'
import { CODIGO_DO_TURNO } from '@modules/aawz-intelligence-v2/constants/Text.busca'

// `d_codigo.trecho` chega com até 4096 caracteres. O bloco do thread mostra o
// começo e deixa o resto para o expandir — um `<pre>` de 4 KB empurra a
// resposta para fora da tela.
const LINHAS_VISIVEIS = 6

/**
 * As primeiras linhas do trecho, e se sobrou algo.
 *
 * @param {String} trecho O código executado.
 * @returns {Object} `{ resumo, temMais, inteiro }`.
 */
function recortarTrecho(trecho) {
  const linhas = String(trecho || '').split('\n')
  return {
    inteiro: String(trecho || ''),
    resumo: linhas.slice(0, LINHAS_VISIVEIS).join('\n'),
    temMais: linhas.length > LINHAS_VISIVEIS,
  }
}

export default {
  name: 'IntelligenceV2CodigoExecutado',
  props: {
    // Os payloads `d_codigo` CRUS, na ordem de chegada (getter
    // `codigoStreaming`). Contrato em `contratos/events_agente.schema.json`.
    eventos: {
      type: Array,
      default: function () {
        return []
      },
    },
  },
  data() {
    return {
      MOLDURA_DO_THREAD,
      CODIGO_DO_TURNO,
      // Nasce ABERTO — o que a IA executou é parte da resposta, não anexo.
      aberto: true,
      expandidos: {},
    }
  },
  computed: {
    temCodigo() {
      return this.eventos.length > 0
    },
    resumo() {
      return String(this.eventos.length)
    },
    classeSeta() {
      return this.aberto ? 'ia-block-seta--aberta' : ''
    },
    itens() {
      const expandidos = this.expandidos
      return this.eventos.map(function (evento, indice) {
        return decorarCodigo(evento, indice, expandidos[indice] === true)
      })
    },
  },
  methods: {
    alternar() {
      this.aberto = !this.aberto
    },
    alternarTrecho(indice) {
      // `$set`: chave nova em objeto reativo do Vue 2 não dispara render sozinha.
      this.$set(this.expandidos, indice, this.expandidos[indice] !== true)
    },
  },
}

/**
 * Um `d_codigo` → a linha pronta para o template.
 *
 * @param {Object} evento O payload do evento `codigo`.
 * @param {Number} indice Posição no turno.
 * @param {Boolean} expandido Se o consultor abriu o trecho inteiro.
 * @returns {Object} A linha decorada.
 */
function decorarCodigo(evento, indice, expandido) {
  const recorte = recortarTrecho(evento.trecho)
  return {
    chave: indice,
    indice: indice,
    linguagem: evento.linguagem || '',
    // ONDE rodou: o consultor precisa saber quando não foi na AAWZ.
    onde: CODIGO_DO_TURNO.SANDBOX[evento.sandbox] || '',
    trecho: expandido ? recorte.inteiro : recorte.resumo,
    podeAlternar: recorte.temMais,
    rotuloAlternar: expandido ? CODIGO_DO_TURNO.VER_MENOS : CODIGO_DO_TURNO.VER_MAIS,
    saida: evento.stdout_resumo || '',
    // Recusado ANTES de rodar (`eventos_harness.py::montar_evento_codigo`):
    // sem esta frase o bloco mostraria um trecho vazio e pareceria um bug.
    frase: evento.motivo ? CODIGO_DO_TURNO.RECUSADO(evento.motivo) : '',
  }
}
</script>

<template>
  <div v-if="temCodigo" class="codigo-executado ia-block-card" v-bind="MOLDURA_DO_THREAD">
    <button
      type="button"
      class="ia-block-card__cabecalho ia-block-card__cabecalho--fita ia-block-card__cabecalho--clicavel"
      :aria-expanded="String(aberto)"
      :aria-label="CODIGO_DO_TURNO.ARIA_LISTA"
      data-test="codigo-executado-cabecalho"
      @click="alternar"
    >
      <icon-svg
        name="monitor"
        :size="14"
        color="var(--primary)"
        class="ia-block-card__icone"
      />
      <span class="ia-block-card__titulo">{{ CODIGO_DO_TURNO.TITULO }}</span>
      <span class="ia-block-card__meta">{{ resumo }}</span>
      <icon-svg
        name="arrow-down-new-ux"
        :size="14"
        color="var(--neutral-700)"
        class="ia-block-seta"
        :class="classeSeta"
      />
    </button>

    <div
      v-for="item in itens"
      v-show="aberto"
      :key="item.chave"
      class="codigo-executado__item"
      data-test="codigo-executado-item"
    >
      <div class="codigo-executado__topo">
        <BaseStatus soft preserve-case color="secondary" :content="item.linguagem" />
        <span v-if="item.onde" class="codigo-executado__onde ia-texto-auxiliar">{{ item.onde }}</span>
      </div>

      <p
        v-if="item.frase"
        class="codigo-executado__frase ia-texto-auxiliar"
        role="alert"
        data-test="codigo-executado-frase"
      >
        {{ item.frase }}
      </p>

      <pre v-if="item.trecho" class="codigo-executado__trecho ia-texto-mono">{{ item.trecho }}</pre>

      <button
        v-if="item.podeAlternar"
        type="button"
        class="codigo-executado__alternar ia-texto-selo"
        data-test="codigo-executado-alternar"
        @click="alternarTrecho(item.indice)"
      >
        {{ item.rotuloAlternar }}
      </button>

      <div v-if="item.saida" class="codigo-executado__saida">
        <span class="codigo-executado__saida-rotulo ia-texto-selo">{{ CODIGO_DO_TURNO.SAIDA }}</span>
        <pre class="codigo-executado__trecho ia-texto-mono">{{ item.saida }}</pre>
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.codigo-executado
  margin-top: 12px

// Reset do `button` do cabeçalho (ver `busca-web.vue`).
.ia-block-card__cabecalho
  width: 100%
  text-align: left
  font: inherit
  border: 0

.ia-block-card__cabecalho--fita
  border-bottom: 1px solid var(--border-hairline)

.codigo-executado__item
  display: flex
  flex-direction: column
  gap: 6px
  padding: 10px 14px

.codigo-executado__item + .codigo-executado__item
  border-top: 1px solid var(--border-hairline)

.codigo-executado__topo
  display: flex
  align-items: center
  flex-wrap: wrap
  gap: 8px

.codigo-executado__frase
  color: var(--neutral-700)
  margin: 0

// Superfície rebaixada da família quente, a mesma do chip de fontes — o código
// é citação, não conteúdo primário.
.codigo-executado__trecho
  color: var(--neutral-900)
  margin: 0
  padding: 8px 10px
  border-radius: var(--radius-sm)
  background: var(--ia-recessed-bg)
  // Linha longa rola no bloco em vez de esticar a bolha inteira.
  overflow-x: auto

.codigo-executado__saida
  display: flex
  flex-direction: column
  gap: 3px

.codigo-executado__saida-rotulo
  color: var(--neutral-700)
  text-transform: uppercase
  letter-spacing: 0.03em

.codigo-executado__alternar
  color: var(--primary)
  align-self: flex-start
  padding: 0
  border: 0
  background: none
  cursor: pointer

  &:hover
    text-decoration: underline

// A cor do papel (leva 2 da escala: a classe `ia-texto-*` não carrega cor).
.codigo-executado__onde
  color: var(--neutral-700)

</style>
